import React, { useRef, useState, useCallback } from 'react';
import AvatarDefault from '../../../../assets/medium_default_optimized.png';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../assets/founding_creator_icon.png';
import FollowButton from '../../../../components/FollowButton';
import { useClickOutside } from '../../../../hooks/useOutsideClick';
import { useFollowUserMutation } from '../../../../apis/queries/useFollowUserMutation';
import { useNavigate } from 'react-router-dom';
import useTruncatedElement from '../../../../hooks/useTruncatedElement';

const ContributorDetail = ({ articleId, user, index }) => {
  const dropdownRef = useRef(null);
  const { mutate: followUser, isPending: _isFollowingUser } = useFollowUserMutation(articleId);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const currentLoggedInUserId = JSON.parse(localStorage.getItem('user'))?.id;
  const hoverTimeoutRef = useRef(null);

  const readMoreRef = useRef(null);
  const { isTruncated, isReadingMore, toggleIsShowingMore } = useTruncatedElement({
    ref: readMoreRef,
    minLength: 100
  });

  useClickOutside(dropdownRef, () => {
    setOpen(false);
  });

  const handleFollowUser = () => {
    followUser({
      userId: user.id,
      isFollowing: user.isFollowing,
      writerUsername: user.username
    });
  };

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setOpen(true);
    }, 100); // Add a small delay for smoother transition
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 100);
  }, []);

  // Calculate dropdown position
  const getDropdownPosition = () => {
    const baseOffset = 60;
    const indexOffset = index % 3;
    const leftOffset = baseOffset * indexOffset;
    return { left: `-${leftOffset}px` };
  };

  return (
    <div className="relative">
      <div
        key={user.id}
        className="flex items-center relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          navigate(`/${user.username}`);
        }}
      >
        <img
          src={user.imgUrl || AvatarDefault}
          alt={user.firstName}
          className="w-6 h-6 mr-2 rounded-full border-2 border-white object-cover z-0 flex-shrink-0"
        />
        <span className="cursor-pointer text-sm font-[600] z-0">{user.firstName || ''}</span>
      </div>
      <div
        ref={dropdownRef}
        className={`${open ? 'opacity-100 visible' : 'opacity-0 invisible'} absolute top-[0px] transition-all duration-200 ease-in-out`}
        style={{ ...getDropdownPosition(), zIndex: 20 * (index + 1) }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="h-[30px] w-[50%] cursor-pointer"
          onClick={() => navigate(`/${user.username}`)}
        ></div>
        <div className="bg-white w-[288px] rounded-[12px] border border-solid border-[#F2F3F7] shadow-[0px_8px_12px_0px_#80808026] p-[16px]">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate(`/${user.username}`)}
          >
            <img
              src={user.imgUrl || AvatarDefault}
              alt={user.firstName}
              className="w-[54px] h-[54px] rounded-full border-2 border-white object-cover flex-shrink-0"
            />
            <div className="min-w-[0px]">
              <div className="flex items-center gap-2 ">
                <span className="font-[600] text-sm hover:text-growth-green-500 transition duration-200 truncate">
                  {user.firstName || ''}
                </span>
                {user.isVerifiedAuthor && (
                  <img src={VerifiedIcon} alt="verified" className="w-[32px] h-[32px]" />
                )}
                {user.isFoundingCreator && (
                  <img
                    src={FoundingCreatorIcon}
                    alt="Founding Creator"
                    title="Founding Creator"
                    className="w-[32px] h-[32px]"
                  />
                )}
              </div>
              {user.username && (
                <span className="font-[600] text-xs text-02-dim-gray">@{user.username}</span>
              )}
            </div>
          </div>
          <div className="mt-[8px] w-full flex-row">
            <pre
              ref={readMoreRef}
              className={`font-sm text-sm 2xl:text-base font-ui p-0 bg-white italic text-black ${
                !isReadingMore && 'line-clamp-4'
              } whitespace-pre-wrap`}
            >
              {user.description || 'No description'}
            </pre>

            {/* handle Read More */}
            {isTruncated && (
              <div onClick={toggleIsShowingMore} className="cursor-pointer">
                <p className="text-xs !text-02-dim-gray cursor-pointer float-right hover:opacity-80 mt-[-2px]">
                  {isReadingMore ? 'Show less' : 'Read more'}
                </p>
              </div>
            )}
          </div>
          <div className="mt-[8px] flex items-center gap-2">
            <p className="font-[800]">Role:</p>
            <p className="text-[14px]">{user.roleName}</p>
          </div>
          <div className="mt-[8px]">
            {currentLoggedInUserId === user.id ? (
              <></>
            ) : (
              <FollowButton
                isFollowing={user.isFollowing}
                onClick={handleFollowUser}
                isLoading={false}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContributorDetail;
